import { cloneDeep } from 'lodash';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import {
  IProposalMedicalDetails,
  IProposalPageFormState,
} from '../../page/medical/form';
import { IProposalDetailsSummary } from '../../page/expat/index2';
import { validateMedicalDetailsField } from '../../page/medical/validation';
import SelectFormField from '@components/form-fields/SelectFormField';
import MultiselectFormField from '@components/form-fields/MultiselectFormField';
import { MAIN_THEME } from '@styles';

interface IProposalMedicalDetailsWidgetProps {
  pageState: IProposalPageFormState;
  onPageStateUpdate: (pageState: IProposalPageFormState) => void;
  disabledForm?: boolean;
  lovs: Record<string, Record<string, string>>;
  data: IProposalDetailsSummary;
}

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    marginTop: '1em',
  },
  section: {
    padding: '23px 0 25px',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: `repeat(3, 32%)`,
    gap: '2%',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  fullWidthItem: {
    gridColumn: '1 / -1', // This makes the item span all columns
  },
  labelHeader: {
    fontSize:`${MAIN_THEME.fontSize.sm} !important`,
    lineHeight: '16px !important',
    fontFamily: `${MAIN_THEME.font.medium} !important`,
  },
}));

const ProposalMedicalDetailsWidget: React.FC<
  IProposalMedicalDetailsWidgetProps
> = ({ pageState, onPageStateUpdate, disabledForm, data, lovs }) => {
  const { classes } = useStyles();

  const values = pageState.values.medicalDetails;
  const errors = pageState.errors.medicalDetails;
  const touched = pageState.touched.medicalDetails;

  const onFieldUpdate = (
    fieldName: keyof IProposalMedicalDetails,
    value: IProposalMedicalDetails[keyof IProposalMedicalDetails],
    touchedField = false
  ) => {
    const newPageState = cloneDeep(pageState);
    (newPageState.values.medicalDetails as any)[fieldName] = value;
    newPageState.errors.medicalDetails[fieldName] = validateMedicalDetailsField(
      fieldName,
      value,
      data?.LineId.ExternalCode
    );
    if (touchedField) {
      newPageState.touched.medicalDetails[fieldName] = true;
    }
    onPageStateUpdate(newPageState);
  };

  return (
    <WidgetPaper>
      <WidgetSection title="Medical Details" isFieldsGrid fieldsPerRow={3}>
        <SelectFormField
          key="class"
          name="class"
          title="Class*"
          placeholder="Class"
          value={values.class}
          error={touched.class ? errors.class : ''}
          onChange={(v) => onFieldUpdate('class', v, true)}
          disabled={disabledForm}
          selectOptions={lovs.classes}
          material
        />

        <SelectFormField
          key="level"
          name="level"
          title="Level*"
          placeholder="Level"
          value={values.level}
          error={touched.level ? errors.level : ''}
          onChange={(v) => onFieldUpdate('level', v, true)}
          disabled={disabledForm}
          selectOptions={lovs.levels}
          material
        />

        <SelectFormField
          key="cnss"
          name="cnss"
          title="CNSS*"
          placeholder="CNSS"
          value={values.cnss}
          error={touched.cnss ? errors.cnss : ''}
          onChange={(v) => onFieldUpdate('cnss', v, true)}
          disabled={disabledForm}
          selectOptions={lovs.cnssOptions}
          material
        />

        <MultiselectFormField
          key="policyCover"
          name="policyCover"
          title="Policy Cover*"
          placeholder="Policy Cover"
          values={values.policyCover}
          error={touched.policyCover ? errors.policyCover : ''}
          onChange={(v) => onFieldUpdate('policyCover', v, true)}
          disabled={disabledForm}
          selectOptions={lovs.policyCovers}
          material
          className={classes.fullWidthItem}
        />
      </WidgetSection>
    </WidgetPaper>
  );
};

export default ProposalMedicalDetailsWidget;
